import { useMemo, useState } from 'react'
import { ArrowDownRight, ArrowUpRight, LineChart, Receipt, RefreshCw, Sparkles, TrendingDown, TrendingUp } from 'lucide-react'
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { useMetrics } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import { formatCents, formatPercent, relativeTime } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { MetricsLive, OneOffSale } from '@/types'

interface SyncResponse {
  userId: string
  live: MetricsLive
  snapshot: unknown
  syncedAt: string
  error?: string
}

export function Metrics() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useMetrics()
  const live = data?.live
  const history = data?.history ?? []
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)

  const sync = useMutation({
    mutationFn: () => unwrap<SyncResponse>(apiClient.post('/metrics/sync')),
    onSuccess: (res) => {
      setLastSyncedAt(res.syncedAt)
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      const mrr = formatCents(res.live.mrrCents)
      const direct =
        res.live.oneOffPurchases30d > 0
          ? ` · ${formatCents(res.live.oneOffRevenue30dCents)} direct (30d, ${res.live.oneOffPurchases30d})`
          : ''
      toast.success(
        'Stripe synced',
        `${mrr} MRR · ${res.live.activeSubscriptions} active${direct}`,
      )
    },
    onError: (e) => toast.error('Sync failed', (e as Error).message),
  })

  const trend = useMemo(
    () =>
      history.map((h) => ({
        date: h.date.slice(5),
        mrr: h.mrr / 100,
        active: h.activeSubscriptions,
        trialing: h.trialingSubscriptions,
      })),
    [history],
  )

  // Quick insights derivations
  const arpa = live && live.activeSubscriptions > 0
    ? live.mrrCents / live.activeSubscriptions
    : 0
  const growth30d = useMemo(() => {
    if (!live || history.length === 0) return null
    const thirty = history.find(
      (h) => Date.parse(h.date) <= Date.now() - 30 * 86_400_000,
    )
    if (!thirty || thirty.mrr === 0) return null
    return (live.mrrCents - thirty.mrr) / thirty.mrr
  }, [live, history])
  const churn30d = history[history.length - 1]?.churnRate ?? 0

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Business Metrics</h1>
          <p className="text-xs text-jarvis-muted mt-1">
            Live from Stripe · {history.length} day{history.length === 1 ? '' : 's'} of history
            {lastSyncedAt && ` · synced ${relativeTime(lastSyncedAt)}`}
          </p>
        </div>
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:border-jarvis-blue/60 hover:bg-jarvis-blue/5 hover:text-jarvis-blue transition disabled:opacity-50"
        >
          <RefreshCw size={14} className={sync.isPending ? 'animate-spin' : ''} />
          {sync.isPending ? 'Syncing Stripe…' : 'Sync Stripe'}
        </button>
      </header>

      {/* Hero stats — MRR is the headline; ARR/Active/Trialing/PastDue support it */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HeroStat
          label="Monthly Recurring"
          value={live ? formatCents(live.mrrCents) : '—'}
          delta={
            live && live.netNewMrrCents !== 0
              ? {
                  value: `${live.netNewMrrCents >= 0 ? '+' : '-'}${formatCents(Math.abs(live.netNewMrrCents))}`,
                  direction: live.netNewMrrCents >= 0 ? 'up' : 'down',
                  caption: 'vs yesterday',
                }
              : undefined
          }
          primary
        />
        <HeroStat
          label="Annualized"
          value={live ? formatCents(live.arrCents) : '—'}
          caption="MRR × 12"
        />
        <HeroStat
          label="Active"
          value={live ? live.activeSubscriptions : '—'}
          caption={live ? `${live.newSubscriptions} new today` : undefined}
        />
        <HeroStat
          label="Trialing"
          value={live ? live.trialingSubscriptions : '—'}
          caption={live?.pastDueSubscriptions ? `${live.pastDueSubscriptions} past due` : undefined}
        />
      </div>

      {/* Quick insights — small grid of derived KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <InsightTile
          label="ARPA"
          value={arpa > 0 ? formatCents(arpa) : '—'}
          hint="Avg revenue / account"
        />
        <InsightTile
          label="30-day growth"
          value={growth30d !== null ? formatPercent(growth30d) : '—'}
          hint="MRR vs 30 days ago"
          direction={growth30d ?? 0}
        />
        <InsightTile
          label="30-day churn"
          value={formatPercent(churn30d)}
          hint="Lost / starting active"
          direction={-churn30d}
        />
        <InsightTile
          label="Pipeline"
          value={live ? `${live.trialingSubscriptions} trials` : '—'}
          hint="Trials × ARPA"
          subtle={live && arpa ? formatCents(live.trialingSubscriptions * arpa) : undefined}
        />
      </div>

      {/* Direct Sales — one-off, non-subscription purchases */}
      <JarvisCard>
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted flex items-center gap-2">
            <Receipt size={11} className="text-jarvis-blue" />
            Direct Sales
            <span className="text-jarvis-muted/60 normal-case tracking-normal">
              one-off purchases · last 30 days
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <InsightTile
            label="Today"
            value={live ? formatCents(live.oneOffRevenueTodayCents) : '—'}
            hint={live ? `${live.oneOffPurchasesToday} ${live.oneOffPurchasesToday === 1 ? 'sale' : 'sales'}` : ''}
          />
          <InsightTile
            label="Last 7 days"
            value={live ? formatCents(live.oneOffRevenue7dCents) : '—'}
            hint={live ? `${live.oneOffPurchases7d} ${live.oneOffPurchases7d === 1 ? 'sale' : 'sales'}` : ''}
          />
          <InsightTile
            label="Last 30 days"
            value={live ? formatCents(live.oneOffRevenue30dCents) : '—'}
            hint={live ? `${live.oneOffPurchases30d} ${live.oneOffPurchases30d === 1 ? 'sale' : 'sales'}` : ''}
          />
          <InsightTile
            label="Avg sale"
            value={
              live && live.oneOffPurchases30d > 0
                ? formatCents(Math.round(live.oneOffRevenue30dCents / live.oneOffPurchases30d))
                : '—'
            }
            hint="30-day average"
          />
        </div>

        {live && live.recentOneOffSales.length > 0 ? (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-jarvis-muted mb-2">
              Recent sales
            </div>
            <div className="space-y-1">
              {live.recentOneOffSales.map((s) => (
                <SaleRow key={s.id} sale={s} />
              ))}
            </div>
          </div>
        ) : (
          !isLoading && (
            <div className="text-sm text-jarvis-muted py-3 text-center border border-dashed border-jarvis-border/60 rounded-lg">
              No one-off purchases in the last 30 days yet.
            </div>
          )
        )}
      </JarvisCard>

      {/* Trend chart — always rendered; empty array still draws axes */}
      <JarvisCard>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
            MRR Growth
          </h2>
          <span className="text-xs text-jarvis-muted tabular">
            {history.length === 0
              ? 'No history yet — sync once or wait for the 00:05 UTC snapshot'
              : `${history.length} snapshots`}
          </span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="mrr-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: '#0F172A',
                  border: '1px solid #1F2937',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#9CA3AF' }}
                formatter={(v: number) => [`$${v.toLocaleString()}`, 'MRR']}
              />
              <Area
                type="monotone"
                dataKey="mrr"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#mrr-gradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {!isLoading && history.length === 0 && (
          <div className="text-center py-8 -mt-56 relative pointer-events-none">
            <LineChart size={28} className="mx-auto text-jarvis-muted/40 mb-2" />
            <p className="text-sm text-jarvis-muted">No metric history yet.</p>
            <p className="text-xs text-jarvis-muted/70 mt-1 max-w-sm mx-auto">
              Jarvis snapshots Stripe nightly at 00:05 UTC. Click <span className="text-jarvis-blue">Sync Stripe</span> above
              to seed today's data right now.
            </p>
          </div>
        )}
      </JarvisCard>

      {/* Funnel + recent activity in a 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <JarvisCard>
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4 flex items-center gap-2">
            Subscription Funnel
          </h2>
          {isLoading ? (
            <div className="text-jarvis-muted text-sm">Loading…</div>
          ) : (
            <FunnelBar
              steps={[
                { label: 'Trialing', value: live?.trialingSubscriptions ?? 0, tone: 'amber' },
                { label: 'Active', value: live?.activeSubscriptions ?? 0, tone: 'green' },
                { label: 'Past due', value: live?.pastDueSubscriptions ?? 0, tone: 'red' },
              ]}
            />
          )}
        </JarvisCard>

        <JarvisCard>
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">
            Today
          </h2>
          {isLoading ? (
            <div className="text-jarvis-muted text-sm">Loading…</div>
          ) : (
            <div className="space-y-2 text-sm tabular">
              <Row label="New subscriptions" value={live?.newSubscriptions ?? 0} />
              <Row label="Canceled" value={live?.canceledSubscriptions ?? 0} />
              <Row
                label="Net new MRR"
                value={live ? formatCents(live.netNewMrrCents) : '$0'}
                emphasis={live && live.netNewMrrCents !== 0 ? (live.netNewMrrCents > 0 ? 'good' : 'bad') : undefined}
              />
              <Row label="Active total" value={live?.activeSubscriptions ?? 0} />
              <Row label="In trial" value={live?.trialingSubscriptions ?? 0} />
            </div>
          )}
        </JarvisCard>
      </div>

      <p className="text-xs text-jarvis-muted text-center pt-2 flex items-center justify-center gap-1.5">
        <Sparkles size={11} className="text-jarvis-blue" />
        Ask Jarvis on a call: <span className="text-jarvis-text">"sync my Stripe numbers"</span> or{' '}
        <span className="text-jarvis-text">"how's MRR looking?"</span>
      </p>
    </div>
  )
}

interface HeroStatProps {
  label: string
  value: string | number
  delta?: { value: string; direction: 'up' | 'down'; caption: string }
  caption?: string
  primary?: boolean
}

function HeroStat({ label, value, delta, caption, primary }: HeroStatProps) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4 transition-colors',
        primary
          ? 'border-jarvis-blue/30 bg-gradient-to-br from-jarvis-blue/10 to-transparent'
          : 'border-jarvis-border bg-jarvis-surface',
      )}
    >
      <div className="text-[10px] uppercase tracking-widest text-jarvis-muted">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular tracking-tight">{value}</div>
      {delta && (
        <div
          className={cn(
            'mt-1 text-xs flex items-center gap-1 tabular',
            delta.direction === 'up' ? 'text-jarvis-green' : 'text-jarvis-red',
          )}
        >
          {delta.direction === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {delta.value}
          <span className="text-jarvis-muted">{delta.caption}</span>
        </div>
      )}
      {!delta && caption && (
        <div className="mt-1 text-xs text-jarvis-muted">{caption}</div>
      )}
    </div>
  )
}

interface InsightTileProps {
  label: string
  value: string
  hint: string
  subtle?: string
  direction?: number  // sign drives the color
}

function InsightTile({ label, value, hint, subtle, direction }: InsightTileProps) {
  const tone = direction === undefined ? 'neutral' : direction > 0 ? 'good' : direction < 0 ? 'bad' : 'neutral'
  return (
    <div className="rounded-lg border border-jarvis-border bg-jarvis-surface px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-jarvis-muted truncate">
          {label}
        </span>
        {tone === 'good' && <TrendingUp size={11} className="text-jarvis-green flex-shrink-0" />}
        {tone === 'bad' && <TrendingDown size={11} className="text-jarvis-red flex-shrink-0" />}
      </div>
      <div
        className={cn(
          'mt-1.5 text-lg font-semibold tabular',
          tone === 'good' && 'text-jarvis-green',
          tone === 'bad' && 'text-jarvis-red',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-jarvis-muted mt-0.5 truncate">{hint}</div>
      {subtle && (
        <div className="text-[10px] text-jarvis-muted/70 mt-0.5 tabular">{subtle}</div>
      )}
    </div>
  )
}

interface FunnelStep {
  label: string
  value: number
  tone: 'amber' | 'green' | 'red'
}

function FunnelBar({ steps }: { steps: FunnelStep[] }) {
  const max = Math.max(1, ...steps.map((s) => s.value))
  const toneClass: Record<FunnelStep['tone'], string> = {
    amber: 'bg-jarvis-amber/30 border-jarvis-amber/40',
    green: 'bg-jarvis-green/30 border-jarvis-green/40',
    red:   'bg-jarvis-red/30 border-jarvis-red/40',
  }
  return (
    <div className="space-y-3">
      {steps.map((s) => (
        <div key={s.label}>
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-jarvis-text">{s.label}</span>
            <span className="text-jarvis-muted tabular">{s.value}</span>
          </div>
          <div className="h-2 bg-jarvis-surface-2 rounded-full overflow-hidden">
            <div
              className={cn('h-full border-r rounded-full', toneClass[s.tone])}
              style={{ width: `${Math.max(2, (s.value / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function SaleRow({ sale }: { sale: OneOffSale }) {
  const label =
    sale.description ||
    sale.customerEmail ||
    'One-off purchase'
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md hover:bg-jarvis-surface-2 text-sm">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-jarvis-green flex-shrink-0" />
        <span className="truncate text-jarvis-text" title={label}>{label}</span>
        {sale.customerEmail && sale.description && (
          <span className="text-xs text-jarvis-muted truncate">{sale.customerEmail}</span>
        )}
      </div>
      <div className="flex items-baseline gap-3 flex-shrink-0 tabular">
        <span className="text-jarvis-text font-medium">{formatCents(sale.amountCents)}</span>
        <span className="text-xs text-jarvis-muted">{relativeTime(sale.createdAt)}</span>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  emphasis,
}: {
  label: string
  value: string | number
  emphasis?: 'good' | 'bad'
}) {
  return (
    <div className="flex justify-between border-b border-jarvis-border/50 py-2 last:border-0">
      <span className="text-jarvis-muted">{label}</span>
      <span
        className={cn(
          emphasis === 'good' && 'text-jarvis-green',
          emphasis === 'bad' && 'text-jarvis-red',
        )}
      >
        {value}
      </span>
    </div>
  )
}
